import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { Calendar, ChevronLeft, ChevronRight, LayoutList } from 'lucide-react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import FEScheduleCalendar, { toDateKey } from './FEScheduleCalendar'
import { feScheduleService } from '@/lib/services/feScheduleService'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import type { FESchedule } from '@/types/parentTicket'

type ViewMode = 'calendar' | 'list'

function monthBounds(month: Date): { from: string; to: string } {
  const start = new Date(month.getFullYear(), month.getMonth(), 1)
  const end = new Date(month.getFullYear(), month.getMonth() + 1, 0)
  return { from: toDateKey(start), to: toDateKey(end) }
}

function defaultDateRange(): { from: string; to: string } {
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  return { from: toDateKey(start), to: toDateKey(end) }
}

function formatScheduleRange(schedule: FESchedule): string {
  const datePart = schedule.schedule_date?.slice(0, 10) ?? '–'
  const start = schedule.schedule_time?.slice(0, 5)
  const end = schedule.schedule_end_time?.slice(0, 5)
  if (start && end) return `${datePart} ${start}–${end}`
  if (start) return `${datePart} ${start}`
  return datePart
}

function roleBadge(role: FESchedule['role']) {
  return (
    <Badge colorScheme={role === 'primary' ? 'blue' : 'purple'} textTransform="capitalize">
      {role}
    </Badge>
  )
}

export default function FESchedulePage() {
  const colors = useThemeColors()
  const { showError } = useToastNotification()
  const [searchParams, setSearchParams] = useSearchParams()

  const defaults = useMemo(() => defaultDateRange(), [])
  const view = (searchParams.get('view') === 'list' ? 'list' : 'calendar') as ViewMode
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') ?? defaults.from)
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') ?? defaults.to)
  const [feUserId, setFeUserId] = useState(searchParams.get('fe_user_id') ?? '')
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date()
    if (!searchParams.get('date_from')) {
      return new Date(today.getFullYear(), today.getMonth(), 1)
    }
    const seed = searchParams.get('date_from') ?? defaults.from
    const [y, m] = seed.split('-').map(Number)
    return new Date(y, (m ?? 1) - 1, 1)
  })

  const syncUrl = useCallback(
    (next: Partial<{ view: ViewMode; date_from: string; date_to: string; fe_user_id: string }>) => {
      const params = new URLSearchParams(searchParams)
      const merged = {
        view,
        date_from: dateFrom,
        date_to: dateTo,
        fe_user_id: feUserId,
        ...next,
      }
      params.set('view', merged.view)
      params.set('date_from', merged.date_from)
      params.set('date_to', merged.date_to)
      if (merged.fe_user_id) params.set('fe_user_id', merged.fe_user_id)
      else params.delete('fe_user_id')
      params.delete('status')
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams, view, dateFrom, dateTo, feUserId],
  )

  const filters = useMemo(
    () => ({
      date_from: dateFrom,
      date_to: dateTo,
      fe_user_id: feUserId ? Number(feUserId) : undefined,
    }),
    [dateFrom, dateTo, feUserId],
  )

  const { data: schedules = [], isLoading, isFetching, error } = useQuery({
    queryKey: ['fe-schedules', filters],
    queryFn: () => feScheduleService.listSchedules(filters),
    enabled: Boolean(dateFrom && dateTo),
  })

  useEffect(() => {
    if (error) {
      showError(error as Error)
    }
  }, [error, showError])

  const setView = (nextView: ViewMode) => {
    syncUrl({ view: nextView })
  }

  const applyFilters = () => {
    syncUrl({ date_from: dateFrom, date_to: dateTo, fe_user_id: feUserId })
    const [y, m] = dateFrom.split('-').map(Number)
    setCalendarMonth(new Date(y, (m ?? 1) - 1, 1))
  }

  const shiftCalendarMonth = (delta: number) => {
    const next = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + delta, 1)
    const range = monthBounds(next)
    setCalendarMonth(next)
    setDateFrom(range.from)
    setDateTo(range.to)
    syncUrl({ date_from: range.from, date_to: range.to, fe_user_id: feUserId })
  }

  return (
    <Box p={4}>
      <Card bg={colors.cardBg} borderColor={colors.border}>
        <CardHeader pb={2}>
          <HStack justify="space-between" flexWrap="wrap" gap={3}>
            <Heading size="md" color={colors.heading}>
              FE Schedule
            </Heading>
            <ButtonGroup size="sm" isAttached variant="outline">
              <Button
                leftIcon={<Calendar size={16} />}
                colorScheme={view === 'calendar' ? 'blue' : undefined}
                onClick={() => setView('calendar')}
              >
                Calendar
              </Button>
              <Button
                leftIcon={<LayoutList size={16} />}
                colorScheme={view === 'list' ? 'blue' : undefined}
                onClick={() => setView('list')}
              >
                List
              </Button>
            </ButtonGroup>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <HStack align="end" flexWrap="wrap" spacing={3}>
              <FormControl maxW="180px">
                <FormLabel fontSize="sm">From</FormLabel>
                <Input
                  type="date"
                  size="sm"
                  value={dateFrom}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setDateFrom(e.target.value)}
                />
              </FormControl>
              <FormControl maxW="180px">
                <FormLabel fontSize="sm">To</FormLabel>
                <Input
                  type="date"
                  size="sm"
                  value={dateTo}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setDateTo(e.target.value)}
                />
              </FormControl>
              <FormControl maxW="160px">
                <FormLabel fontSize="sm">FE User ID</FormLabel>
                <Input
                  size="sm"
                  placeholder="Optional"
                  value={feUserId}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFeUserId(e.target.value.replace(/\D/g, ''))}
                />
              </FormControl>
              <Button size="sm" colorScheme="blue" onClick={applyFilters} alignSelf="end">
                Apply
              </Button>
            </HStack>

            {isLoading || isFetching ? (
              <HStack justify="center" py={10}>
                <Spinner />
                <Text color={colors.textMuted}>Loading schedules…</Text>
              </HStack>
            ) : view === 'calendar' ? (
              <Box>
                <HStack justify="space-between" mb={3}>
                  <IconButton
                    aria-label="Previous month"
                    size="sm"
                    icon={<ChevronLeft size={16} />}
                    onClick={() => shiftCalendarMonth(-1)}
                  />
                  <IconButton
                    aria-label="Next month"
                    size="sm"
                    icon={<ChevronRight size={16} />}
                    onClick={() => shiftCalendarMonth(1)}
                  />
                </HStack>
                <FEScheduleCalendar schedules={schedules} month={calendarMonth} />
              </Box>
            ) : (
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Date / Time</Th>
                      <Th>Field Engineer</Th>
                      <Th>Role</Th>
                      <Th>Ticket</Th>
                      <Th>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {schedules.length === 0 ? (
                      <Tr>
                        <Td colSpan={5}>
                          <Text color={colors.textMuted} py={4} textAlign="center">
                            No schedules found for the selected range.
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      schedules.map((schedule) => (
                        <Tr key={schedule.id}>
                          <Td whiteSpace="nowrap">{formatScheduleRange(schedule)}</Td>
                          <Td>{schedule.field_engineer?.name ?? `FE #${schedule.fe_user_id}`}</Td>
                          <Td>{roleBadge(schedule.role)}</Td>
                          <Td maxW="280px">
                            <Link
                              as={RouterLink}
                              to={`/tickets/${schedule.ticket_id}/view`}
                              color="blue.500"
                              noOfLines={1}
                            >
                              {schedule.ticket_subject ?? `Ticket #${schedule.ticket_id}`}
                            </Link>
                          </Td>
                          <Td>
                            {schedule.external_dispatch_status ? (
                              <Badge textTransform="capitalize">{schedule.external_dispatch_status}</Badge>
                            ) : (
                              '–'
                            )}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}
